import SVG from "react-inlinesvg";
import { RouteComponentProps } from "react-router";
import { Link, withRouter } from "react-router-dom";
import { Dropdown } from "buba-components-ts";
import cx from "classnames";
import { History } from "history";

import HamburgerIcon from "../../../../assets/icons/other/hamburger.svg";
import LogOutIcon from "../../../../assets/icons/other/logout.svg";
import useScrollState from "../hooks/useScrollState";

import styles from "./Header.scss";

const getRoutes = ({ push }: History, links: Links) =>
  links
    .filter(({ label }: { label: string }) => !!label)
    .map(({ label, path }: { label: string; path: string }) => ({
      text: () => label,
      onClick: (closePopover: () => void) => {
        push(path);
        closePopover();
      },
      title: label
    }));

const getActiveIndex = ({ location }: { location: { pathname: string } }, links: Links) =>
  links.findIndex(route => location.pathname.startsWith(route.path));

export type Links = Array<{
  path: string;
  label: string;
}>;

export type Logo = {
  path: string;
  link?: string;
};

type HeaderProps = RouteComponentProps & {
  logos: Logo[];
  links?: Links;
  history: {
    push: (route: string) => void;
    location: {
      pathname: string;
    };
  };
  areLinksVisible?: boolean;
  logOutLink?: string;
  customContainerStyles: string;
  customLogoStyles: string;
  customLogoContainerStyles?: string;
  customHeaderStyles: string;
  type?: "portal" | "inquiry";
  isNarrowHeader?: boolean;
};

const Header = ({
  logos,
  links = [],
  history,
  areLinksVisible = false,
  logOutLink,
  customContainerStyles = "",
  customLogoStyles = "",
  customLogoContainerStyles = "",
  customHeaderStyles = "",
  type = "portal",
  isNarrowHeader = false
}: HeaderProps) => {
  const routes = getRoutes(history, links);
  const activeIndex = getActiveIndex(history, links);
  const { isScrolledToTop } = useScrollState();

  return (
    <div
      className={cx(styles.headerContainer, {
        [styles.portalHeaderContainer]: type === "portal",
        [styles.inquiryHeaderContainer]: type === "inquiry"
      })}
    >
      <header
        className={cx(
          styles.header,
          {
            [styles.narrowHeader]: !isScrolledToTop || isNarrowHeader,
            [styles.portalHeader]: type === "portal",
            [styles.inquiryHeader]: type === "inquiry"
          },
          customHeaderStyles
        )}
      >
        <div className={cx(styles.contentContainer, customContainerStyles)}>
          {logos.map((logo, index) => (
            <Link
              key={`logo-${logo.path}`}
              to={logo.link || "/"}
              className={cx(styles.logoContainer, customLogoContainerStyles, {
                [styles.portalLogoContainer]: type === "portal",
                [styles.inquiryLogoContainer]: type === "inquiry"
              })}
              data-testid={`logo-${index}`}
            >
              <img className={cx(styles.logo, customLogoStyles)} src={logo.path} alt="logo" />
            </Link>
          ))}
          {areLinksVisible && links.length > 0 && (
            <div className={styles.navContainer}>
              <nav className={styles.nav}>
                {links.map(({ path, label }, linkIndex) => (
                  <span className={styles.linkContainer} key={label}>
                    <Link
                      to={path}
                      className={cx(styles.link, {
                        [styles.activeLink]: activeIndex === linkIndex
                      })}
                    >
                      {label}
                    </Link>
                  </span>
                ))}
              </nav>
              {logOutLink && (
                <Link to={logOutLink} className={styles.logOutLink} data-testid="portal:logOut">
                  <SVG className={styles.logOutIcon} src={LogOutIcon} />
                </Link>
              )}
              {routes.length > 0 && (
                <div className={styles.dropdownContainer}>
                  <Dropdown
                    options={[
                      ...routes,
                      {
                        text: () => "Abmeldung",
                        onClick: () => logOutLink && history.push(logOutLink),
                        title: "Abmeldung"
                      }
                    ]}
                    activeIndex={activeIndex}
                    icon={HamburgerIcon}
                    popperWidth={120}
                    onLeft
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </header>
    </div>
  );
};

export default withRouter(Header);
